// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import Dexie, { type Table } from 'dexie'
import type {
  UserProfile,
  TrustedPerson,
  AccessKey,
  KeyPermissions,
  NextOfKinSession,
  AccessLogEntry,
  FailedAttemptRecord,
  MessageEnvelope,
  InactivityProtocol,
  Wish,
  FuneralWishes,
  MedicalWishes,
  DocumentIndexEntry,
  DigitalEstateEntry,
  PersonalFarewell,
  AISignal,
  TimelineEvent,
  ModuleProgress,
  SyncQueueItem,
} from '@/types'

export class WilsAppDatabase extends Dexie {
  // Tables
  user_profiles!:        Table<UserProfile,        string>
  trusted_persons!:      Table<TrustedPerson,       string>
  access_keys!:          Table<AccessKey,           string>
  key_permissions!:      Table<KeyPermissions,      string>
  nextofkin_sessions!:   Table<NextOfKinSession,    string>
  access_log!:           Table<AccessLogEntry,      string>
  failed_attempts!:      Table<FailedAttemptRecord, string>
  messages!:             Table<MessageEnvelope,     string>
  inactivity_protocols!: Table<InactivityProtocol,  string>
  wishes!:               Table<Wish,                string>
  funeral_wishes!:       Table<FuneralWishes,       string>
  medical_wishes!:       Table<MedicalWishes,       string>
  document_index!:       Table<DocumentIndexEntry,  string>
  digital_estate!:       Table<DigitalEstateEntry,  string>
  personal_farewell!:    Table<PersonalFarewell,     string>
  ai_signals!:           Table<AISignal,            string>
  timeline_events!:      Table<TimelineEvent,       string>
  module_progress!:      Table<ModuleProgress,      string>
  sync_queue!:           Table<SyncQueueItem,       string>

  constructor() {
    super('WilsAppDB')

    // ── Version 1: MVP baseline ──────────────────────────────────────────────
    this.version(1).stores({
      user_profiles:
        'id, cloudProfileId, syncStatus, schemaVersion',

      trusted_persons:
        'id, &accessKeyId, userId, [userId+role], syncStatus',

      access_keys:
        'id, userId, [userId+keyType], activationStatus, invalidatedAt, syncStatus',

      key_permissions:
        'id, &accessKeyId, userId, syncStatus',

      nextofkin_sessions:
        'id, accessKeyId, userId, status, expiresAt',

      access_log:
        'id, userId, occurredAt, [userId+occurredAt], eventType, outcome, retainUntil, actorSessionId, syncStatus',

      failed_attempts:
        'id, targetUserId, deviceFingerprint, blockedUntil, attemptedAt',

      messages:
        'id, userId, draftStatus, releasedAt, syncStatus',

      inactivity_protocols:
        '&userId, id, enabled',

      wishes:
        'id, userId, [userId+category], completionStatus, syncStatus',

      funeral_wishes:
        '&userId, id, completionStatus, syncStatus',

      medical_wishes:
        '&userId, id, completionStatus, syncStatus',

      document_index:
        'id, userId, [userId+documentCategory], syncStatus',

      digital_estate:
        'id, userId, [userId+serviceCategory], syncStatus',

      personal_farewell:
        '&userId, id, completionStatus, syncStatus',

      ai_signals:
        'id, userId, [userId+status], priority, expiresAt',

      timeline_events:
        'id, userId, [userId+occurredAt], eventCategory, syncStatus',

      module_progress:
        'id, &[userId+moduleName], userId, status, syncStatus',

      sync_queue:
        'id, [tableName+recordId], status, priority, queuedAt',
    })
  }
}

// Singleton instance
export const db = new WilsAppDatabase()

// Graceful error handling during open
db.open().catch((err) => {
  console.error('WilsAppDB failed to open:', err)
})
